import { apiGetApprovedMap, getCurrentList, listFlags } from 'src/features/wbs/api';
import type { ProjectSetting } from 'src/features/projects/types';
import type { WbsFlatItem, WbsItemVersion } from 'src/features/wbs/types';
import { buildRollupRows } from 'src/features/wbs/lib/progressRollup';
import { buildCurrentWeekDisplayMap, resolveCurrentProvisionalEarnedValue } from 'src/features/wbs/lib/liveEv';
import { buildWeightedValueMap } from 'src/features/wbs/lib/weeklyEvDisplay';
import { formatProjectDateOnly, getProjectActiveWeekAnchorDateOnly } from 'src/utils/date';
import type { GanttData } from '../types/gantt.type';

type CurrentListResponse = { list: any; items: WbsItemVersion[] } | null;

function isoToYmd(iso?: string | null, zone?: string): string | null {
    if (!iso) return null;
    return formatProjectDateOnly(iso, zone, '') || null;
}

function getEffectiveAnchorDow(setting?: ProjectSetting | null, now = new Date()) {
    const current = setting?.wbsWeekAnchorDow ?? 5;
    const pending = setting?.wbsWeekAnchorDowPending ?? null;
    const from = setting?.wbsWeekAnchorEffectiveFrom ? new Date(setting.wbsWeekAnchorEffectiveFrom) : null;
    if (pending != null && from && now >= from) return pending;
    return current;
}

function toFlatRows(items: WbsItemVersion[], zone: string): WbsFlatItem[] {
    return items.map((it) => ({
        id: it.nodeId,
        nodeId: it.nodeId,
        parentId: it.parentNodeId ?? null,
        level: it.level,
        code: it.code ?? '',
        name: it.name ?? '',
        plannedStart: isoToYmd(it.plannedStart, zone),
        plannedFinish: isoToYmd(it.plannedFinish, zone),
        actualWeight: it.weight != null ? Number(it.weight) : undefined,
        plannedValue: it.plannedProgress != null ? Number(it.plannedProgress) : null,
        earnedValue: it.actualProgress != null ? Number(it.actualProgress) : null,
        memo: it.memo ?? undefined,
    }));
}

export async function fetchWbsRowsForGantt(
    projectId: string,
    opts?: { zone?: string; setting?: ProjectSetting | null },
): Promise<GanttData> {
    const zone = opts?.zone ?? 'Asia/Seoul';
    const res = (await getCurrentList(projectId)) as CurrentListResponse;
    const items = res?.items ?? [];
    const anchorDow = getEffectiveAnchorDow(opts?.setting);
    const anchorDate = getProjectActiveWeekAnchorDateOnly(new Date(), anchorDow, zone);

    if (!items.length) {
        return { anchorDate, rows: [] };
    }

    const flatRows = toFlatRows(items, zone);
    const nodeIds = flatRows.map((row) => row.nodeId);
    const previousAnchor = getProjectActiveWeekAnchorDateOnly(new Date(new Date(`${anchorDate}T00:00:00`).getTime() - 7 * 86400000), anchorDow, zone);

    const [flags, approvedMap, approvedMapW1] = await Promise.all([
        listFlags(projectId, anchorDate, nodeIds),
        apiGetApprovedMap(projectId, anchorDate, nodeIds),
        apiGetApprovedMap(projectId, previousAnchor, nodeIds),
    ]);

    const currentRows = flatRows.map((row) => ({
        ...row,
        liveEarnedValue: resolveCurrentProvisionalEarnedValue(row, flags ?? {}, approvedMap ?? {}, approvedMapW1 ?? {}),
    }));

    const rolledRows = buildRollupRows(currentRows as WbsFlatItem[]);
    const currentWeekMap = buildCurrentWeekDisplayMap(currentRows, flags ?? {}, approvedMapW1 ?? {});
    const confirmedMap = buildWeightedValueMap(currentRows, (row) => row.earnedValue ?? null);

    return {
        anchorDate,
        rows: rolledRows.map((row) => ({
            id: row.nodeId,
            parentId: row.parentId ?? null,
            level: row.level,
            code: row.code ?? undefined,
            name: row.name ?? undefined,
            plannedStart: row.plannedStart ?? null,
            plannedFinish: row.plannedFinish ?? null,
            plannedValue: row.plannedValue ?? null,
            displayEarnedValue: currentWeekMap[row.id] ?? null,
            earnedValue: currentWeekMap[row.id] ?? null,
            confirmedEarnedValue: confirmedMap[row.id] ?? null,
            remarks: row.memo ?? undefined,
        })),
    };
}
